import { styled } from '@mui/material/styles';
import { Box, Collapse } from '@mui/material';
import { t } from 'i18next';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, UNSAFE_NavigationContext } from 'react-router-dom';
import { PrimaryButton, SecondaryButtonRegular } from './Button';
import { ConfirmationOverlay } from '@/pages/company/disable/ConfirmationOverlay';
import { SemiBold, SMRegular } from '@/typography/Typography';

const ButtonContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.greyBorder}`,
    backgroundColor: theme.palette.background.paper
}));

const RightButtons = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75)
}));

interface FormActionButtonsProps {
    show: boolean;
    onSave: () => void;
    onCancel: () => void;
    isLoading?: boolean;
    saveLabel?: string;
    cancelLabel?: string;
    leftActions?: React.ReactNode;
    rightActions?: React.ReactNode;
    alwaysShowRightActions?: boolean;
}

export const FormActionButtons: React.FC<FormActionButtonsProps> = ({
    show,
    onSave,
    onCancel,
    isLoading = false,
    saveLabel = t('save_changes'),
    cancelLabel = t('cancel'),
    leftActions,
    rightActions,
    alwaysShowRightActions = false
}) => {
    const buttonContainerRef = useRef<HTMLDivElement>(null);
    const [showNavigationDialog, setShowNavigationDialog] = useState(false);
    const [pendingLocation, setPendingLocation] = useState<string | null>(null);
    const navigate = useNavigate();
    const navigationContext = React.useContext(UNSAFE_NavigationContext);

    // Block navigation when form is dirty (show is true)
    useEffect(() => {
        if (!show || !navigationContext) return;

        const { navigator } = navigationContext;
        const originalPush = navigator.push;
        const originalReplace = navigator.replace;

        // Override push method
        navigator.push = (to: any, _state?: any) => {
            setPendingLocation(typeof to === 'string' ? to : to.pathname);
            setShowNavigationDialog(true);
        };

        // Override replace method
        navigator.replace = (to: any, _state?: any) => {
            setPendingLocation(typeof to === 'string' ? to : to.pathname);
            setShowNavigationDialog(true);
        };

        return () => {
            navigator.push = originalPush;
            navigator.replace = originalReplace;
        };
    }, [show, navigationContext]);

    // Handle browser refresh/close when form is dirty
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (show) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () =>
            window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [show]);

    const handleNavigationCancel = useCallback(() => {
        setShowNavigationDialog(false);
        setPendingLocation(null);
    }, []);

    const handleNavigationContinue = useCallback(() => {
        setShowNavigationDialog(false);
        if (pendingLocation) {
            // Temporarily disable blocking to allow navigation
            const currentShow = show;
            if (currentShow) {
                // Call onCancel to reset form state before navigating
                onCancel();
            }
            // Navigate after a brief delay to ensure state is updated
            setTimeout(() => {
                navigate(pendingLocation);
            }, 0);
        }
        setPendingLocation(null);
    }, [pendingLocation, navigate, show, onCancel]);

    useEffect(() => {
        if (show && buttonContainerRef.current) {
            // Small delay to allow the collapse animation to start
            const timeoutId = setTimeout(() => {
                buttonContainerRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end'
                });
            }, 310);

            return () => clearTimeout(timeoutId);
        }
    }, [show]);

    return (
        <>
            <Collapse in={show} timeout={300} unmountOnExit>
                <ButtonContainer ref={buttonContainerRef}>
                    <Box>{leftActions}</Box>
                    <RightButtons>
                        {!alwaysShowRightActions && rightActions}
                        <SecondaryButtonRegular
                            onClick={onCancel}
                            disabled={isLoading}
                        >
                            {cancelLabel}
                        </SecondaryButtonRegular>
                        <PrimaryButton
                            size="medium"
                            loading={isLoading}
                            onClick={onSave}
                            sx={{ marginLeft: '6px !important' }}
                        >
                            {saveLabel}
                        </PrimaryButton>
                    </RightButtons>
                </ButtonContainer>
            </Collapse>
            {alwaysShowRightActions && rightActions && (
                <ButtonContainer>
                    <Box />
                    <RightButtons>{rightActions}</RightButtons>
                </ButtonContainer>
            )}
            <ConfirmationOverlay
                open={showNavigationDialog}
                displayAreYouSureText={false}
                p1={
                    <SemiBold mb={1} textAlign="center">
                        Save changes before closing?
                    </SemiBold>
                }
                p2={
                    <SMRegular textAlign="center">
                        {t('unsaved_changes_warning')}
                    </SMRegular>
                }
                danger
                onCancel={handleNavigationCancel}
                onContinue={handleNavigationContinue}
                continueText={t('leave_page')}
            />
        </>
    );
};
